"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { Calendar, ChevronLeft, ChevronRight, Newspaper } from "lucide-react"

interface NewsItem {
    nid: number
    img: string | null
    post_date: number
    title_pt: string
    title_en: string | null
    content_pt: string
    content_en: string | null
}

export default function NewsPage() {
    const [news, setNews] = useState<NewsItem[]>([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)

    useEffect(() => {
        const fetchNews = async () => {
            setLoading(true)
            try {
                const res = await fetch(`/api/news?limit=9&page=${page}`)
                if (res.ok) {
                    const data = await res.json()
                    setNews(data.news)
                    setTotalPages(data.pages)
                }
            } catch {
                console.error("Failed to fetch news")
            } finally {
                setLoading(false)
            }
        }
        fetchNews()
    }, [page])

    const getTitle = (item: NewsItem) => item.title_en || item.title_pt
    const getExcerpt = (item: NewsItem) => {
        const content = item.content_en || item.content_pt
        const text = content?.replace(/<[^>]*>/g, "") || ""
        return text.length > 160 ? text.substring(0, 160) + "…" : text
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
            <div className="container mx-auto px-4 py-16">
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                        <Newspaper className="w-3 h-3" />
                        News Archive
                    </div>
                    <h1 className="text-5xl font-black tracking-tighter text-white mb-4 uppercase">
                        Latest <span className="text-blue-500">Chronicles</span>
                    </h1>
                    <p className="text-slate-400 text-lg max-w-md mx-auto italic">
                        All updates and announcements from the world of DragonL2.
                    </p>
                </div>

                {loading ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="rounded-2xl border border-slate-800/50 bg-slate-900/40 p-6 animate-pulse">
                                <div className="h-48 bg-slate-800/50 rounded-xl mb-4" />
                                <div className="h-4 bg-slate-800/50 rounded w-1/3 mb-3" />
                                <div className="h-6 bg-slate-800/50 rounded w-3/4 mb-3" />
                                <div className="h-4 bg-slate-800/50 rounded w-full mb-2" />
                                <div className="h-4 bg-slate-800/50 rounded w-2/3" />
                            </div>
                        ))}
                    </div>
                ) : news.length === 0 ? (
                    <div className="text-center py-20">
                        <Newspaper className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                        <p className="text-slate-500 text-lg">No news articles published yet.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {news.map((item) => (
                                <div
                                    key={item.nid}
                                    className="group relative rounded-2xl border border-slate-800/50 bg-slate-900/40 overflow-hidden hover:border-blue-500/50 transition-all hover:shadow-2xl hover:shadow-blue-500/10"
                                >
                                    <div className="h-48 w-full overflow-hidden relative">
                                        <img
                                            src={item.img || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&q=80&w=800"}
                                            alt={getTitle(item)}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
                                    </div>

                                    <div className="p-6">
                                        <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase tracking-widest mb-3">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {new Date(item.post_date * 1000).toLocaleDateString("en-US", {
                                                year: "numeric",
                                                month: "short",
                                                day: "numeric",
                                            })}
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-3 line-clamp-2 leading-tight group-hover:text-blue-400 transition-colors">
                                            <Link href={`/news/${item.nid}`}>
                                                <span className="absolute inset-0" />
                                                {getTitle(item)}
                                            </Link>
                                        </h3>
                                        <p className="text-slate-400 text-sm leading-relaxed line-clamp-3">
                                            {getExcerpt(item)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-12">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="p-2 rounded-lg border border-slate-800 text-slate-400 hover:text-white hover:border-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        className={`w-10 h-10 rounded-lg text-sm font-bold transition-colors ${
                                            p === page
                                                ? "bg-blue-500 text-white"
                                                : "border border-slate-800 text-slate-400 hover:text-white hover:border-slate-600"
                                        }`}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="p-2 rounded-lg border border-slate-800 text-slate-400 hover:text-white hover:border-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
